import React, { ChangeEvent, useLayoutEffect, useState } from "react";
import Steps from "common/Steps/Steps.tsx";
import Modal from "common/Modal/Modal.tsx";
import Input from "common/Inputs/Input.tsx";
import styled from "styled-components";
import Text from "common/Typography/Text";
import { sharedLib } from "@shared/services/shared.service.ts";
import {
  copyCodeToClipboard,
  displayResultMessage,
  openMessage,
} from "utils/misc.tsx";
import Title from "common/Typography/Title.tsx";
import AnimatedCheckIcon from "common/Animations/AnimatedCheckIcon.tsx";
import {
  ExternalAccountInvitation,
  ExternalInvitationType,
  LanguageNameSpaces,
  UserImpersonationPermissionLevel,
} from "@shared/types";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils.ts";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import InputBox from "common/Inputs/InputBox.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { joinWords } from "@shared/common/i18n/utils";
import Select from "common/Select/Select.tsx";
import { INVITATION_PERMISSION_TO_DISPLAY } from "features/configuration/common/constants.ts";
import {
  useGetExternalInviteLink,
  useShowPermissionOptions,
} from "features/configuration/InviteExternalUsers/hooks.tsx";
import { ErrorText } from "features/bots/common/styles.ts";

interface CreateInvitationModalProps {
  open?: boolean;
  onCancel?: () => void;
  invitation?: ExternalAccountInvitation;
}

const CreateInvitationModal = ({
  open,
  onCancel,
  invitation,
}: CreateInvitationModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const [step, setStep] = useState(Step.CREATE);
  const [name, setName] = useState("");
  const [createAccountInvite, createAccountInviteStatus] =
    sharedLib.sharedApi.configuration.useCreateAccountInviteMutation();
  const {
    showClientPermissionOptions,
    showAssetManagerPermissionOptions,
    isCommunityAdmin,
    isAssetManager,
    isDefaultPermissions,
    hasWritePermissionFlag,
  } = useShowPermissionOptions();
  const [assetManagerPermission, setAssetManagerPermission] = useState(
    isCommunityAdmin
      ? UserImpersonationPermissionLevel.READ
      : UserImpersonationPermissionLevel.WRITE
  );
  const [clientPermission, setClientPermission] = useState(
    isCommunityAdmin
      ? UserImpersonationPermissionLevel.WRITE
      : isAssetManager
      ? UserImpersonationPermissionLevel.READ
      : UserImpersonationPermissionLevel.NONE
  );
  const isShareStep = step === Step.SHARE;
  const { getExternalInviteLink } = useGetExternalInviteLink();

  const getPermissionsError = () => {
    if (isCommunityAdmin) return undefined;
    const clientIsWrite =
      clientPermission === UserImpersonationPermissionLevel.WRITE;
    const assetManagerIsWrite =
      assetManagerPermission === UserImpersonationPermissionLevel.WRITE;
    if (!clientIsWrite && !assetManagerIsWrite) {
      return "Write access is required. Please enable write permissions for either the Asset Manager or the Client to continue.";
    } else if (clientIsWrite && assetManagerIsWrite) {
      return "Only one write permission is allowed. Please disable write access for either the Asset Manager or the Client to continue.";
    }
  };

  const permissionErrorMessage = getPermissionsError();

  const validateData = () => {
    return !name.trim() || !!permissionErrorMessage;
  };

  useLayoutEffect(() => {
    if (invitation && !name) {
      setName(invitation.name);
    }
  }, [invitation]);

  const getPermissionsOptions = (isClientPermissions?: boolean) => {
    const permissions: UserImpersonationPermissionLevel[] = [];
    if (isClientPermissions) {
      permissions.push(UserImpersonationPermissionLevel.NONE);
    }
    permissions.push(UserImpersonationPermissionLevel.READ);
    if (!isClientPermissions || hasWritePermissionFlag) {
      permissions.push(UserImpersonationPermissionLevel.WRITE);
    }
    return permissions.map((permission) => {
      return {
        value: permission,
        label: INVITATION_PERMISSION_TO_DISPLAY[permission],
      };
    });
  };

  const getAssetManagerAccessDropdown = () => {
    const options = getPermissionsOptions();
    const title = t("invitations.asset_manager_access");
    return (
      <div style={{ marginTop: 20 }}>
        <InputTitle>{title}</InputTitle>
        <PermissionsWrapper></PermissionsWrapper>
        <Select
          style={{ width: "100%" }}
          value={assetManagerPermission}
          onChange={setAssetManagerPermission}
          popupMatchSelectWidth={false}
          optionLabelProp="label"
          options={options}
          mobileTitle={title}
        />
      </div>
    );
  };

  const getClientAccessDropdown = () => {
    const title = t("invitations.client_access");
    return (
      <div style={{ marginTop: 20 }}>
        <InputTitle>{title}</InputTitle>
        <Select
          style={{ width: "100%" }}
          value={clientPermission}
          onChange={setClientPermission}
          popupMatchSelectWidth={false}
          optionLabelProp="label"
          options={getPermissionsOptions(true)}
          mobileTitle={title}
        />
      </div>
    );
  };

  const getCreateStep = () => {
    return (
      <>
        <InputTitle>{t("invitations.account_name")}</InputTitle>
        <Input
          value={name}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
        />
        {showAssetManagerPermissionOptions && getAssetManagerAccessDropdown()}
        {showClientPermissionOptions && getClientAccessDropdown()}
        {permissionErrorMessage && (
          <ErrorText>{permissionErrorMessage}</ErrorText>
        )}
      </>
    );
  };

  const closeModal = () => {
    onCancel?.();
    setName("");
    setStep(Step.CREATE);
  };

  const handleCreateInvitation = () => {
    if (isShareStep) {
      closeModal();
    } else {
      displayResultMessage(
        createAccountInvite({
          name: name.trim(),
          type: ExternalInvitationType.EXTERNAL_INVITE,
          permission_level: isDefaultPermissions ? undefined : clientPermission,
          asset_manager_permission_level: isDefaultPermissions
            ? undefined
            : assetManagerPermission,
        }).unwrap()
      )
        .then(() => setStep(Step.SHARE))
        .catch(() => {});
    }
  };

  const shareLink = getExternalInviteLink(
    createAccountInviteStatus.data?.token
  );

  const getShareStep = () => {
    return (
      <>
        <div style={{ textAlign: "center" }}>
          <AnimatedCheckIcon size={100} />
          <Title level={2} style={{ margin: "12px 0 28px 0" }}>
            {joinWords([
              t("invitations.invitation"),
              invitation ? t("invitations.updated") : t("invitations.created"),
              t("invitations.successfully"),
            ])}
          </Title>
        </div>
        <InputBox
          style={{ marginBottom: 12 }}
          onClick={() => {
            copyCodeToClipboard(shareLink).catch(() => {});
            openMessage(getCopyMessage(t("invitations.invite_link")), "info");
          }}
        >
          <Text
            type="secondary"
            $ellipsis={true}
            style={{
              flex: 1,
            }}
          >
            {shareLink}
          </Text>
          <CopyOutlined style={{ paddingLeft: 8 }} />
        </InputBox>
        <Text>{t("invitations.copy_link_description")}</Text>
      </>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={closeModal}
      title={joinWords([
        invitation ? t("invitations.update") : t("invitations.create"),
        t("invitations.invitation"),
      ])}
      okText={
        isShareStep
          ? undefined
          : invitation
          ? t("invitations.save")
          : t("invitations.create")
      }
      onOk={handleCreateInvitation}
      okButtonProps={{
        disabled: validateData(),
        loading: createAccountInviteStatus.isLoading,
      }}
      destroyOnHidden={true}
    >
      <Steps
        current={step}
        style={{ marginBottom: "24px" }}
        items={[
          {
            title: invitation
              ? t("invitations.update")
              : t("invitations.create"),
          },
          {
            title: t("invitations.share"),
          },
        ]}
      />
      {step === Step.CREATE && getCreateStep()}
      {step === Step.SHARE && getShareStep()}
    </Modal>
  );
};

export default CreateInvitationModal;

enum Step {
  CREATE,
  SHARE,
}

const InputTitle = styled(Text)`
  margin-bottom: 8px;
`;
const PermissionsWrapper = styled.div`
  display: flex;
  column-gap: 8px;
`;
